import { createBrowserRouter } from "react-router";
import { wrapCreateBrowserRouterV7 } from "@sentry/react";
import { RootLayout } from "./components/layout/root-layout";
import { RouteErrorBoundary } from "./components/error-boundary";
import { RequireAction } from "./components/require-action";
import { EventRedirectPage } from "./pages/event-redirect";
import { FleetOverviewPage } from "./pages/fleet-overview";
import { BuildDetailPage } from "./pages/build-detail";
import { CalibrationSyncPage } from "./pages/calibration-sync";
import { TeamReportPage } from "./pages/team-report";
import { AuditLogPage } from "./pages/audit-log";
import { AdminPhotoCategoriesPage } from "./pages/admin-photo-categories";
import { AdminReleasesPage } from "./pages/admin-releases";
import { AdminEventsPage } from "./pages/admin-events";
import { EventReleasesPage } from "./pages/event-releases";
import { CommonCalsPage } from "./pages/common-cals";
import { WingCalsPage } from "./pages/wing-cals";
import { CommonCalsEditorPage } from "./pages/common-cals-editor";
import { FleetMeasurementsIndex } from "./pages/fleet-measurements";
import { YawPage } from "./pages/fleet-measurements/yaw";
import { SteeringPage } from "./pages/fleet-measurements/steering";
import { DbHeightPage } from "./pages/fleet-measurements/db-height";
import { BoatWeighingsPage } from "./pages/boat-weighings";
import { PlatformWeighingPage } from "./pages/platform-weighing";
import { WingWeighingPage } from "./pages/wing-weighing";
import { FleetEqualizationPage } from "./pages/fleet-equalization";
import { FleetWeighingsPage } from "./pages/fleet-weighings";
import { SchedulePage } from "./pages/schedule/schedule-page";
import { FleetMetricsPage } from "./pages/fleet-metrics";
import { FleetReportPage } from "./pages/fleet-report";
import { NotFoundPage } from "./pages/not-found";
import { ServicingRedirectPage } from "./pages/servicing-redirect";

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <EventRedirectPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId",
        element: <RequireAction action="view-builds"><FleetOverviewPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/team",
        element: <TeamReportPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/schedule",
        element: <SchedulePage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "admin/calibration-sync",
        element: <RequireAction action="manage-calibrations"><CalibrationSyncPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "admin/photo-categories",
        element: <RequireAction action="manage-calibrations"><AdminPhotoCategoriesPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "admin/releases",
        element: <RequireAction action="manage-releases"><AdminReleasesPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "admin/events",
        element: <RequireAction action="manage-events"><AdminEventsPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/releases",
        element: <RequireAction action="manage-releases"><EventReleasesPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/releases/:releaseId/common-cals",
        element: <RequireAction action="manage-releases"><CommonCalsEditorPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/fleet-measurements",
        element: <RequireAction action="view-fleet"><FleetMeasurementsIndex /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/fleet-measurements/yaw",
        element: <RequireAction action="view-fleet"><YawPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/fleet-measurements/steering",
        element: <RequireAction action="view-fleet"><SteeringPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/fleet-measurements/db-height",
        element: <RequireAction action="view-fleet"><DbHeightPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/common-cals",
        element: <RequireAction action="manage-releases"><CommonCalsPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/wing-cals",
        element: <RequireAction action="manage-calibrations"><WingCalsPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/builds/:buildId",
        element: <RequireAction action="view-builds"><BuildDetailPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "events/:eventId/builds/:buildId/audit",
        element: <RequireAction action="view-builds"><AuditLogPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "boats/:boatId/weighings",
        element: <RequireAction action="view-builds"><BoatWeighingsPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "boats/:boatId/weighings/platform/new",
        element: <RequireAction action="view-builds"><PlatformWeighingPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "boats/:boatId/weighings/platform/:weighingId",
        element: <RequireAction action="view-builds"><PlatformWeighingPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "boats/:boatId/weighings/wing/new",
        element: <RequireAction action="view-builds"><WingWeighingPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "boats/:boatId/weighings/wing/:weighingId",
        element: <RequireAction action="view-builds"><WingWeighingPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "fleet-report",
        element: <RequireAction action="view-fleet-report"><FleetReportPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "metrics",
        element: <RequireAction action="view-builds"><FleetMetricsPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        // Parts and Maintenance moved to servicing.sailgp.tech at the
        // 2026-09-28 cutover, on the same paths: old links land on the same
        // page there. No guard: servicing checks access itself.
        path: "parts/*",
        element: <ServicingRedirectPage />,
      },
      {
        path: "boats/:boatId/parts",
        element: <ServicingRedirectPage />,
      },
      {
        path: "events/:eventId/fleet-equalization",
        element: <RequireAction action="view-fleet"><FleetEqualizationPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "admin/weighings",
        element: <RequireAction action="view-builds"><FleetWeighingsPage /></RequireAction>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "maintenance/*",
        element: <ServicingRedirectPage />,
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
]);
